import { createGlobalStyle } from 'styled-components';

const GlobalStyle = createGlobalStyle`
    :root {
        /* Cores principais */
        --primary-color: #2c3e50;
        --secondary-color: #4a90e2;
        --accent-color: #d3455b;
        --success-color: #27ae60;
        --warning-color: #f39c12;
        --danger-color: #e74c3c;
        --info-color: #3498db;
        
        /* Tons de cinza */
        --gray-100: #f1f1f1;
        --gray-200: #e9ecef;
        --gray-300: #dee2e6;
        --gray-400: #ced4da;
        --gray-500: #6c757d;
        --gray-600: #495057;
        --gray-700: #343a40;
        --gray-800: #212529;
        --gray-900: #000000;
        
        /* Cores de fundo */
        --bg-light: #ffffff;
        --bg-main: #f1f1f1;
        --bg-dark: #444444;
        
        /* Cores de texto */
        --text-primary: #000000;
        --text-secondary: #6c757d;
        --text-light: #ffffff;
        --text-muted: #495057;
        
        /* Tipografia */
        --font-family: 'Arial', Helvetica, sans-serif;
        --font-size-xs: 12px;
        --font-size-sm: 14px;
        --font-size-base: 16px;
        --font-size-lg: 18px;
        --font-size-xl: 20px;
        --font-size-2xl: 24px;
        --font-size-3xl: 30px;
        --font-weight-normal: 400;
        --font-weight-medium: 500;
        --font-weight-semibold: 600;
        --font-weight-bold: 700;
        
        /* Espaçamentos */
        --spacing-xs: 4px;
        --spacing-sm: 8px;
        --spacing-md: 12px;
        --spacing-lg: 16px;
        --spacing-xl: 20px;
        --spacing-2xl: 24px;
        --spacing-3xl: 30px;
        --spacing-4xl: 40px;
        
        /* Border radius */
        --border-radius-sm: 4px;
        --border-radius-md: 8px;
        --border-radius-lg: 12px;
        --border-radius-full: 50%;
        
        /* Sombras */
        --shadow-sm: 0 1px 3px rgba(0, 0, 0, 0.1);
        --shadow-md: 0 2px 8px rgba(0, 0, 0, 0.08);
        --shadow-lg: 0 4px 12px rgba(0, 0, 0, 0.15);
        --shadow-xl: 0 8px 24px rgba(0, 0, 0, 0.2);
    }

    body{
        input::-webkit-outer-spin-button,
        input::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
        }

        /* Firefox */
        input[type="number"] {
            -moz-appearance: textfield;
            appearance: textfield;
        }

        padding: 0px;
        margin: 0px;
        font-family: var(--font-family);
        background: var(--bg-main);

        /* Compensar o menu lateral fixo */
        &.with-sidebar {
            margin-left: 240px;
            transition: margin-left 0.3s ease;
        }

        /* Quando o menu está colapsado */
        &.with-sidebar.sidebar-collapsed {
            margin-left: 60px;
        }

        /* Garantir que o conteúdo principal tenha espaçamento adequado */
        &.with-sidebar .container-fluid {
            padding-left: 20px;
            padding-right: 20px;
        }

        p {
            color: var(--text-primary);
            margin: 0px;
        }
        
        a {
            text-decoration: none;
            color: var(--text-primary);
        }

        a:hover {
            color: var(--text-primary);
            opacity: 0.7;
        }

        .conShadown {
            box-shadow: 0 3px 10px 0 rgb(0 0 0 / 15%);
        }

        input {
            margin-top: 20px;
            border-radius: 5px;
            padding: 5px;
            font-size: 17px;
            border: none;
            border-bottom: 2px solid #ccc;
            outline:none !important;
        }

        button {
            border-radius: var(--border-radius-sm);
            padding: var(--spacing-sm);
            font-size: var(--font-size-lg);
            border: none;
            border-bottom: 2px solid var(--gray-400);
            outline:none !important;
            background: var(--primary-color);
            color: var(--text-light);
        }

        h2 {
            font-size: var(--font-size-2xl);
            font-weight: var(--font-weight-semibold);
        }

        h3 {
            font-size: var(--font-size-xl);
            font-weight: var(--font-weight-semibold);
        }
    }
`;

export default GlobalStyle;

// Tema da aplicação
export const theme = {
  colors: {
    primary: '#2c3e50',      // Cor principal (azul escuro)
    secondary: '#4a90e2',    // Cor secundária (azul claro)
    accent: '#d3455b',       // Cor de destaque (vermelho)
    success: '#27ae60',      // Verde para sucesso
    warning: '#f39c12',      // Laranja para aviso
    danger: '#e74c3c',       // Vermelho para erro
    info: '#3498db',         // Azul para informação
    
    // Tons de cinza
    gray: {
      100: '#f1f1f1',
      200: '#e9ecef',
      300: '#dee2e6',
      400: '#ced4da',
      500: '#6c757d',
      600: '#495057',
      700: '#343a40',
      800: '#212529',
      900: '#000000'
    },
    
    // Cores de fundo
    background: {
      light: '#ffffff',
      main: '#f1f1f1',
      dark: '#444444'
    },
    
    // Cores de texto
    text: {
      primary: '#000000',
      secondary: '#6c757d',
      light: '#ffffff',
      muted: '#495057'
    }
  },
  
  // Tipografia
  typography: {
    fontFamily: "'Arial', Helvetica, sans-serif",
    fontSize: {
      xs: '12px',
      sm: '14px',
      base: '16px',
      lg: '18px',
      xl: '20px',
      '2xl': '24px',
      '3xl': '30px'
    },
    fontWeight: {
      normal: '400',
      medium: '500',
      semibold: '600',
      bold: '700'
    }
  },
  
  // Espaçamentos
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '20px',
    '2xl': '24px',
    '3xl': '30px',
    '4xl': '40px'
  },
  
  // Border radius
  borderRadius: {
    sm: '4px',
    md: '8px',
    lg: '12px',
    full: '50%'
  },
  
  // Sombras
  shadows: {
    sm: '0 1px 3px rgba(0, 0, 0, 0.1)',
    md: '0 2px 8px rgba(0, 0, 0, 0.08)',
    lg: '0 4px 12px rgba(0, 0, 0, 0.15)',
    xl: '0 8px 24px rgba(0, 0, 0, 0.2)'
  }
};

// Cores antigas para compatibilidade (serão removidas gradualmente)
export const colors = {
  primary: '#4a90e2',
  secondary: '#d3455b',
  background1: '#444444',
  background2: '#eeeeee'
};
